/**
 * MCP Servers List Page.
 *
 * Replaces the static IntegrationsPage placeholder with a real CRUD interface
 * connected to the Sprint 38 backend endpoints.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Icon,
  SearchInput,
  Select,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
  TemplateIcon,
} from '@/components/ui';
import { useMCPServers, useDeleteMCPServer } from '@/features/mcp';
import type { MCPServerSummary, MCPHealthStatus, MCPServerStatus } from '@/features/mcp';
import { Zap, Box } from 'lucide-react';
import { toast } from 'sonner';

// =============================================================================
// Helpers
// =============================================================================

function healthStatusType(health: MCPHealthStatus) {
  switch (health) {
    case 'healthy':
      return 'healthy' as const;
    case 'degraded':
      return 'warning' as const;
    case 'unhealthy':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

function serverStatusType(status: MCPServerStatus) {
  switch (status) {
    case 'active':
      return 'active' as const;
    case 'disabled':
      return 'draft' as const;
    case 'error':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

function tierLabel(tier: string) {
  switch (tier) {
    case 'platform_http':
      return 'Platform';
    case 'byos':
      return 'BYOS';
    case 'local_agent':
      return 'Runner';
    case 'platform_native':
      return 'Internal';
    default:
      return tier;
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// Component
// =============================================================================

export function MCPServersListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useMCPServers({
    page,
    pageSize: 20,
    status: statusFilter || undefined,
    search: search || undefined,
  });

  const deleteMutation = useDeleteMCPServer();

  // api.get unwraps response.data.data, so `data` is either the full envelope
  // (when the API returns {data, total, ...}) or already the array.
  const servers = (Array.isArray(data) ? data : data?.data) ?? [];
  const total = Array.isArray(data) ? (data as MCPServerSummary[]).length : (data?.total ?? 0);
  const totalPages = Math.ceil(total / 20);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(`${name} deleted`);
    } catch {
      toast.error(t('common.error'));
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">
            {t('common.error')}: {(error as Error).message}
          </p>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.integrations.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.integrations.subtitle')}
          </p>
        </div>
        <Button leftIcon="plus" onClick={() => navigate('/integrations/new')}>
          {t('admin.integrations.addServer')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder={t('common.search')}
          className="w-64"
        />
        <Select
          value={statusFilter || 'all'}
          onChange={(e) => {
            setStatusFilter(e.target.value === 'all' ? '' : e.target.value);
            setPage(1);
          }}
          options={[
            { value: 'all', label: t('common.all') },
            { value: 'active', label: t('admin.integrations.statusActive') },
            { value: 'disabled', label: t('admin.integrations.statusDisabled') },
          ]}
          className="w-40"
        />
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('admin.integrations.health')}</TableHead>
              <TableHead>{t('admin.integrations.tier')}</TableHead>
              <TableHead>{t('admin.integrations.tools')}</TableHead>
              <TableHead>{t('common.date')}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {servers.length === 0 ? (
              <TableEmpty
                colSpan={7}
                message={t('admin.integrations.empty')}
              />
            ) : (
              servers.map((server: MCPServerSummary) => (
                <TableRow key={server.id}>
                  {/* Name + description */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <TemplateIcon icon={server.template_icon} size="sm" className="text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/integrations/${server.id}`}
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {server.name}
                          </Link>
                          {server.setup_source && server.setup_source !== 'manual' && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" title={t('admin.integrations.setupSource')}>
                              <Zap className="w-2.5 h-2.5" />
                              {t(`admin.integrations.setupSourceLabel.${server.setup_source}`)}
                            </span>
                          )}
                          {server.setup_source === 'auto_enroll' && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400" title={t('admin.integrations.internal')}>
                              <Box className="w-2.5 h-2.5" />
                              {t('admin.integrations.internal')}
                            </span>
                          )}
                        </div>
                        {server.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-xs">
                            {server.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <StatusBadge
                      status={serverStatusType(server.status)}
                      label={server.status}
                    />
                  </TableCell>

                  {/* Health */}
                  <TableCell>
                    <StatusBadge
                      status={healthStatusType(server.health_status)}
                      label={server.health_status}
                    />
                  </TableCell>

                  {/* Tier */}
                  <TableCell>
                    <span className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                      {tierLabel(server.execution_tier)}
                    </span>
                  </TableCell>

                  {/* Tools count */}
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm">
                      <Icon name="settings" size={14} className="text-gray-400" />
                      {server.tools_count}
                    </span>
                  </TableCell>

                  {/* Date */}
                  <TableCell className="text-sm text-gray-500">
                    {formatDate(server.created_at)}
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    {server.setup_source !== 'auto_enroll' && (
                      <button
                        onClick={() => handleDelete(server.id, server.name)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title={t('common.delete')}
                      >
                        <Icon name="trash-2" size={16} />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500">
              {t('common.showing', {
                from: (page - 1) * 20 + 1,
                to: Math.min(page * 20, total),
                total,
              })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
